import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { QuestionEntityQuery, QuestionEntityService, QuestionStoreModel, TopLevelFormGroup } from '@examdojo/question';
import { ReferenceQuestionService } from '@examdojo/reference-question';
import { debounceTime, distinctUntilChanged, filter, forkJoin, map, of, switchMap, tap } from 'rxjs';

@Injectable()
export class QuestionEditorService {
  constructor(
    private readonly questionEntityQuery: QuestionEntityQuery,
    private readonly questionEntityService: QuestionEntityService,
    private readonly referenceQuestionService: ReferenceQuestionService,
  ) {
    this.fetchQuestionOnIdChange().pipe(takeUntilDestroyed()).subscribe();
  }

  readonly activeQuestionFetched$ = this.questionEntityQuery.selectActive().pipe(map(Boolean), distinctUntilChanged());

  readonly questionForm = new FormGroup({
    question_items: new FormArray<TopLevelFormGroup>([]),
    status: new FormControl<QuestionStoreModel['status']>('DRAFT', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  private fetchQuestionOnIdChange() {
    return this.questionEntityQuery.selectActiveId().pipe(
      filter(isNotNullish),
      debounceTime(0),
      distinctUntilChanged(),
      switchMap((id) =>
        forkJoin([
          this.questionEntityService.fetchQuestionDetails(id, {
            setActive: true,
            resetStore: true,
          }),
        ]),
      ),
      tap(([questionStoreModel]) => {
        if (!questionStoreModel) {
          return;
        }

        this.questionForm.controls.status.setValue(questionStoreModel.question.status, {
          emitEvent: false,
        });
      }),
      switchMap(([questionStoreModel]) =>
        questionStoreModel
          ? this.referenceQuestionService.fetch(questionStoreModel.question.reference_question_id)
          : of(null),
      ),
    );
  }
}
